import { LegalLayout, LegalSection } from "@/components/LegalLayout";

export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" updated="June 1, 2026" current="/privacy">
      <p>
        NextGen Resolution Experts ("we," "us," or "our") respects your privacy. This Privacy Policy explains what
        information we collect, how we use it, and the choices you have. It applies to our website, AI helpdesk
        platform, and related services (the "Service").
      </p>

      <LegalSection heading="1. Information We Collect">
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Account information:</strong> name, work email address, company name, and role — collected when you register or are invited to a workspace.</li>
          <li><strong>Support content:</strong> tickets, chat messages with our AI assistant, and attachments you submit so we can provide support.</li>
          <li><strong>Lead information:</strong> name, email, company, and message details submitted through our contact/sales form.</li>
          <li><strong>Usage data:</strong> log data such as pages visited, features used, and approximate timestamps, used to improve the Service.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="2. How We Use Your Information">
        <ul className="list-disc pl-6 space-y-2">
          <li>To provide, operate, and maintain the helpdesk Service;</li>
          <li>To resolve support tickets and power AI-assisted responses;</li>
          <li>To communicate with you about your account, tickets, and service updates;</li>
          <li>To respond to sales inquiries you initiate;</li>
          <li>To secure the platform and prevent abuse.</li>
        </ul>
        <p><strong>We do not sell your personal data. Ever.</strong></p>
      </LegalSection>

      <LegalSection heading="3. How We Store & Protect Your Data">
        <p>
          Your data is stored on secure, professionally managed cloud infrastructure (including Emergent hosting and
          MongoDB databases) with encryption in transit. Passwords are stored using industry-standard one-way
          hashing — we never store your password in plain text. Access to production data is restricted to
          authorized personnel on a need-to-know basis.
        </p>
      </LegalSection>

      <LegalSection heading="4. AI Processing">
        <p>
          Messages sent to the AI assistant are processed by trusted large-language-model providers solely to
          generate responses to your support requests. We do not use your support content to train third-party
          models beyond what is required to deliver the Service.
        </p>
      </LegalSection>

      <LegalSection heading="5. Sharing of Information">
        <p>We share information only with:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Service providers that help us operate the platform (hosting, email delivery, AI processing), under confidentiality obligations;</li>
          <li>Your workspace administrator, who can view tickets and activity within your company's workspace;</li>
          <li>Law enforcement or regulators when required by law.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="6. Data Retention & Deletion">
        <p>
          We retain account and support data for as long as your workspace remains active. Upon termination of a
          Service Agreement, clients may request an export of their customer data, after which data is deleted from
          active systems within a commercially reasonable period.
        </p>
      </LegalSection>

      <LegalSection heading="7. Your Rights">
        <p>
          Depending on your location, you may have the right to access, correct, export, or delete your personal
          data. To exercise these rights, email{" "}
          <a href="mailto:support@nextgenresolutionexperts.com" className="text-blue-600 hover:underline">
            support@nextgenresolutionexperts.com
          </a>{" "}
          and we will respond within 30 days.
        </p>
      </LegalSection>

      <LegalSection heading="8. Cookies">
        <p>
          We use essential cookies and local storage to keep you signed in and to remember your preferences. We do
          not use third-party advertising cookies.
        </p>
      </LegalSection>

      <LegalSection heading="9. Changes to This Policy">
        <p>
          We may update this Privacy Policy periodically. Material changes will be announced via email or in-app
          notice, and the "Last updated" date above will be revised.
        </p>
      </LegalSection>

      <LegalSection heading="10. Contact">
        <p>
          Privacy questions or requests:{" "}
          <a href="mailto:support@nextgenresolutionexperts.com" className="text-blue-600 hover:underline">
            support@nextgenresolutionexperts.com
          </a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
